import React, { useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface ScorecardTrendsChartProps {
  extACatNcPct:    number | null;   // External Cat-A completionPercent
  intACatNcPct:    number | null;   // Internal Cat-A completionPercent
  avgPct:          number | null;   // Fire Safety aggregate %
  nearMissPct:     number | null;   // Near Miss %
  nearMissActual:  number | null;   // Near Miss actual count
  majorPct:        number | null;   // Major Incident %
  majorActual:     number | null;   // Major Incident actual count
  reportDate?:     string;
  premisesName?:   string;
  onCanvasReady?:  (getImage: () => string) => void;
}

// ── Bar value label plugin — shows "pct% (count)" on incident bars ────────────
const barValueLabelPlugin = {
  id: 'trendsBarLabels',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset: any, di: number) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden || !meta?.data?.length) return;
      meta.data.forEach((bar: any, idx: number) => {
        const val: number | null = dataset.data[idx];
        const actual: number | null = dataset._actual ?? null;

        // Always draw something for incident bars (even N/A)
        let label: string;
        if (dataset._isIncident) {
          if (val == null && actual == null) {
            label = 'N/A';
          } else {
            const pctStr = val != null ? `${val.toFixed(1)}%` : 'N/A';
            const cntStr = actual != null ? ` (${actual})` : '';
            label = `${pctStr}${cntStr}`;
          }
        } else {
          if (val == null || val === 0) return;
          label = `${val.toFixed(1)}%`;
        }

        ctx.save();
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'bottom';
        ctx.font         = 'bold 11px Arial';
        ctx.fillStyle    = '#1f2937';
        ctx.fillText(label, bar.x, bar.y - 4);
        ctx.restore();
      });
    });
  },
};

const BARS = [
  { label: '% External Audit (Cat-A NC)', bg: 'rgba(239,68,68,0.80)',   border: 'rgba(220,38,38,1)' },
  { label: '% Internal Audit (Cat-A NC)', bg: 'rgba(251,113,133,0.85)', border: 'rgba(244,63,94,1)' },
  { label: 'Fire Safety Aggregate %',     bg: 'rgba(231,108,13,0.80)',  border: 'rgba(232,150,36,1)' },
  { label: 'Near Miss %',                 bg: 'rgba(68, 202, 239, 0.8)',   border: 'rgba(28, 185, 174, 1)',  isIncident: true },
  { label: 'Major Incident %',            bg: 'rgba(99,102,241,0.80)',  border: 'rgba(79,70,229,1)',  isIncident: true },
];

const ScorecardTrendsChart: React.FC<ScorecardTrendsChartProps> = ({
  extACatNcPct,
  intACatNcPct,
  avgPct,
  nearMissPct,
  nearMissActual,
  majorPct,
  majorActual,
  reportDate,
  premisesName,
  onCanvasReady,
}) => {
  const chartRef = useRef<any>(null);

  // ── Notify parent with getImage() after chart renders ────────────────────
  useEffect(() => {
    if (!onCanvasReady) return;
    const t = setTimeout(() => {
      onCanvasReady(() => {
        const chart = chartRef.current;
        if (!chart) return '';
        const src = chart.canvas as HTMLCanvasElement;
        const off = document.createElement('canvas');
        off.width = src.width; off.height = src.height;
        const ctx = off.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, off.width, off.height);
        ctx.drawImage(src, 0, 0);
        return off.toDataURL('image/png');
      });
    }, 300);
    return () => clearTimeout(t);
  }, [onCanvasReady, extACatNcPct, intACatNcPct, avgPct, nearMissPct, majorPct]);

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownload = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const src = chart.canvas as HTMLCanvasElement;
    const off = document.createElement('canvas');
    off.width = src.width; off.height = src.height;
    const ctx = off.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, off.width, off.height);
    ctx.drawImage(src, 0, 0);
    const a = document.createElement('a');
    a.download = `Scorecard_Trends_${reportDate || 'report'}.png`;
    a.href = off.toDataURL('image/png');
    a.click();
  };

  const subtitleParts = [
    premisesName,
    reportDate
      ? new Date(reportDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : null,
  ].filter(Boolean);

  const groupLabel = reportDate
    ? new Date(reportDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
    : 'Current';

  // Values aligned to BARS array: [extANcPct, intANcPct, aggPct, nearMissPct, majorPct]
  const values = [
    extACatNcPct ?? 0,
    intACatNcPct ?? 0,
    avgPct ?? 0,
    nearMissPct ?? 0,
    majorPct ?? 0,
  ];
  const actuals = [null, null, null, nearMissActual, majorActual];

  const chartData = {
    labels: [groupLabel],
    datasets: BARS.map((bar, i) => ({
      label:           bar.label,
      data:            [values[i]],
      _actual:         actuals[i],
      _isIncident:     bar.isIncident ?? false,
      backgroundColor: bar.bg,
      borderColor:     bar.border,
      borderWidth:     1.5,
      borderRadius:    4,
      categoryPercentage: 0.55,
      barPercentage:      0.75,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 36, left: 8, right: 8, bottom: 8 } },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { font: { size: 12 }, color: '#374151', padding: 16 },
      },
      title: {
        display: true,
        text: subtitleParts.length
          ? ['Fire Safety Scorecard — Snapshot', subtitleParts.join(' · ')]
          : 'Fire Safety Scorecard — Snapshot',
        font: { size: 14, weight: 'bold' as const },
        padding: { top: 4, bottom: 10 },
        color: '#1f2937',
      },
      tooltip: {
        backgroundColor: 'rgba(17,24,39,0.92)',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const ds = ctx.dataset;
            const pct = ctx.parsed.y ?? 0;
            const base = `${ds.label}: ${pct.toFixed(1)}%`;
            if (ds._isIncident && ds._actual != null) {
              return `${base} (${ds._actual} incidents)`;
            }
            return base;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 13, weight: 'bold' as const }, color: '#374151' },
      },
      y: {
        beginAtZero: true,
        max: 110,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: { font: { size: 11 }, color: '#6b7280', callback: (v: any) => `${v}%` },
        title: {
          display: true,
          text: 'Percentage (%)',
          font: { size: 12, weight: 'bold' as const },
          color: '#374151',
        },
      },
    },
  };

  const hasData = values.some(v => v > 0)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-sm font-semibold text-gray-700">Show Trends</span>
          {subtitleParts.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">{subtitleParts.join(' · ')}</p>
          )}
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PNG
        </button>
      </div>

      {!hasData ? (
        <div className="h-80 flex items-center justify-center text-gray-400 text-sm">
          No data available — fill in audit and parameter values first.
        </div>
      ) : (
        <div className="h-96">
          <Bar
            ref={chartRef}
            data={chartData}
            options={chartOptions as any}
            plugins={[barValueLabelPlugin]}
          />
        </div>

          // {/* Custom legend */}
          //  <div className="mt-4 flex flex-wrap gap-3">
          //   {BARS.map(item => (
          //     <div key={item.label} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
          //       <div
          //         className="w-3 h-3 rounded-sm flex-shrink-0"
          //         style={{ backgroundColor: item.bg, border: `1.5px solid ${item.border}` }}
          //       />
          //       <span className="text-xs font-medium text-gray-600">{item.label}</span>
          //     </div>
          //   ))}
          // </div>
      )}
    </div>
  );
};

export default ScorecardTrendsChart;
